import { useTranslation } from "react-i18next";
import { useHrDashboard } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { formatDateTime, money } from "../../lib/format";
import type { HrLevel } from "../../lib/typesHr";
import { SectionHeader } from "../ui/Badges";
import { LiveBoard } from "./AttendancePanel";
import { dateOnly, Kpi, StatusPill } from "./shared";

export type HrTabId = "overview" | "my" | "attendance" | "leave" | "approvals" | "people" | "payroll" | "loans" | "adjustments" | "devices" | "setup";

export function HrOverview({ level, go }: { level: HrLevel; go: (t: HrTabId) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const d = useHrDashboard();
  const x = d.data;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label={t("hr.kpi.headcount")} value={x?.headcount ?? "…"} />
        <Kpi label={t("hr.kpi.present")} value={x?.presentToday ?? "…"} tone="green" hint={x ? `${t("hr.kpi.of", { total: x.headcount })}` : undefined} />
        <Kpi label={t("hr.kpi.onLeave")} value={x?.onLeaveToday ?? "…"} tone="purple" />
        <button type="button" className="text-start" onClick={() => go("approvals")}><Kpi label={t("hr.kpi.approvals")} value={x?.approvalsWaiting ?? "…"} tone={x && x.approvalsWaiting > 0 ? "orange" : "navy"} hint={t("hr.kpi.open")} /></button>
      </div>

      {level >= 2 && x && (x.unknownPins > 0 || x.offlineDevices.length > 0) && (
        <section className="card space-y-2 border-amber-300 bg-amber-50/50 p-5" role="status">
          <SectionHeader icon="⚠️" title={t("hr.kpi.attention")} />
          {x.unknownPins > 0 && <p className="text-xs font-bold text-amber-900">{t("hr.kpi.unknownPins", { count: x.unknownPins })} <button type="button" className="btn-ghost ms-2 !py-0.5" onClick={() => go("devices")}>{t("hr.kpi.fix")}</button></p>}
          {x.offlineDevices.map((dv) => <p key={dv.id} className="text-xs font-bold text-amber-900">{t("hr.kpi.deviceOffline", { name: dv.name })} {dv.last_seen_at ? <span className="num font-medium">({formatDateTime(dv.last_seen_at, locale)})</span> : null}</p>)}
        </section>
      )}

      {level >= 2 && x?.latestPeriod && (
        <section className="card flex flex-wrap items-center justify-between gap-3 p-5">
          <div>
            <div className="text-[10px] font-black uppercase tracking-wider text-gray-500">{t("hr.kpi.latestPayroll")}</div>
            <div className="text-sm font-black">{x.latestPeriod.label} <span className="num text-xs font-semibold text-gray-500">{dateOnly(x.latestPeriod.period_start, locale)} → {dateOnly(x.latestPeriod.period_end, locale)}</span></div>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={x.latestPeriod.status} ns="periodStatus" />
            {x.activeLoans && <span className="text-xs font-bold text-gray-600">{t("hr.kpi.loansOut", { count: x.activeLoans.n })}: <span className="num">{money(Number(x.activeLoans.outstanding))}</span> {t("currency")}</span>}
            <button type="button" className="btn-primary" onClick={() => go("payroll")}>{t("hr.tabs.payroll")}</button>
          </div>
        </section>
      )}
      <LiveBoard />
    </div>
  );
}
